'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@kit/ui/drawer';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { isProfileSetupExemptPath } from '~/lib/tenant/profile-setup-routes';

import { FinalizeSignupFields } from '../../../auth/profile-setup/_components/finalize-signup-fields';
import {
  ProfileSetupForm,
  type ProfileSetupFormProps,
} from '../../../auth/profile-setup/_components/profile-setup-form';
import { useIsNative } from '../../_lib/capacitor/capacitor-context';
import { useNativePresentation } from './_lib/use-native-presentation';

type NativeProfileSetupDialogProps = {
  tenantId: string;
  userId: string;
} & (
  | { pendingSignupFields: true }
  | { pendingSignupFields: false; form: Omit<ProfileSetupFormProps, 'onComplete'> }
);

const dismissalEvent = 'native-profile-setup-dismissed';
const fallbackDismissals = new Set<string>();

function subscribeToDismissal(onChange: () => void) {
  window.addEventListener(dismissalEvent, onChange);
  return () => window.removeEventListener(dismissalEvent, onChange);
}

function isDismissed(key: string) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return fallbackDismissals.has(key);
  }
}

function subscribeToDialogs(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-state'],
  });
  return () => observer.disconnect();
}

function hasOtherDialog() {
  return !!document.querySelector(
    '[role="dialog"][data-state="open"]:not([data-test="native-profile-setup-dialog"])',
  );
}

const hiddenOnServer = () => true;

export function NativeProfileSetupDialog(props: NativeProfileSetupDialogProps) {
  const { isNative } = useIsNative();
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation();
  const presentation = useNativePresentation();
  const [drawer, setDrawer] = useState<HTMLDivElement | null>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const key = `native-profile-setup:${props.tenantId}:${props.userId}`;
  const dismissed = useSyncExternalStore(
    subscribeToDismissal,
    () => isDismissed(key),
    hiddenOnServer,
  );
  // Let the initial location picker (or another active dialog) finish first.
  const otherDialogOpen = useSyncExternalStore(
    subscribeToDialogs,
    hasOtherDialog,
    hiddenOnServer,
  );

  const dismiss = () => {
    fallbackDismissals.add(key);
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // Keep dismissal for this document when WebView storage is unavailable.
    }
    window.dispatchEvent(new Event(dismissalEvent));
  };

  const open =
    isNative &&
    !dismissed &&
    !otherDialogOpen &&
    !isProfileSetupExemptPath(pathname);

  // WKWebView can pan as well as shrink the visible viewport on input focus.
  // Keep the sheet within those bounds and scroll its fields, not the page.
  useEffect(() => {
    if (!open) return;
    const fields = fieldsRef.current;
    if (!drawer || !fields) return;
    const viewport = window.visualViewport;
    let frame = 0;

    const update = () => {
      const height = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      drawer.style.bottom = `${Math.max(0, window.innerHeight - height - offsetTop)}px`;
      drawer.style.maxHeight = `min(80dvh, calc(${height}px - env(safe-area-inset-top, 0px) - 16px))`;

      const focused = document.activeElement;
      if (!(focused instanceof HTMLElement) || !fields.contains(focused)) return;
      const inputBounds = focused.getBoundingClientRect();
      const fieldBounds = fields.getBoundingClientRect();
      if (inputBounds.bottom > fieldBounds.bottom - 8) {
        fields.scrollTop += inputBounds.bottom - fieldBounds.bottom + 8;
      } else if (inputBounds.top < fieldBounds.top + 8) {
        fields.scrollTop -= fieldBounds.top - inputBounds.top + 8;
      }
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    scheduleUpdate();
    viewport?.addEventListener('resize', scheduleUpdate);
    viewport?.addEventListener('scroll', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);
    fields.addEventListener('focusin', scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', scheduleUpdate);
      viewport?.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      fields.removeEventListener('focusin', scheduleUpdate);
    };
  }, [open, drawer]);

  return (
    <Drawer
      open={open}
      repositionInputs={false}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
    >
      <DrawerContent
        ref={setDrawer}
        data-test="native-profile-setup-dialog"
        data-native-surface
        className={cn(presentation.className, 'mx-auto max-w-lg')}
        style={presentation.style}
        blockOutside
        overlayClassName="bg-black/45 backdrop-blur-[2px]"
      >
        <DrawerHeader className="relative shrink-0 px-6 pe-16">
          <DrawerTitle>
            <Trans
              i18nKey="auth:profileSetupHeading"
              defaults="Complete Your Profile"
            />
          </DrawerTitle>
          <DrawerDescription>
            <Trans
              i18nKey="auth:profileSetupReminder"
              defaults="Add the missing details to complete your profile. You can do this now or the next time you open the app."
            />
          </DrawerDescription>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute end-3 top-2 h-11 w-11"
              aria-label={t('common:close', { defaultValue: 'Close' })}
              data-test="native-profile-setup-close"
            >
              <X className="h-5 w-5" />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div
          ref={fieldsRef}
          data-test="native-profile-setup-fields"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-4"
        >
          {props.pendingSignupFields ? (
            <FinalizeSignupFields
              nextPath="/"
              onComplete={() => router.refresh()}
            />
          ) : (
            <ProfileSetupForm
              {...props.form}
              onComplete={() => {
                dismiss();
                router.refresh();
              }}
            />
          )}
        </div>
        <DrawerFooter className="shrink-0 px-6 pt-0">
          <DrawerClose asChild>
            <Button
              variant="ghost"
              className="w-full"
              data-test="native-profile-setup-later"
            >
              <Trans i18nKey="auth:profileSetupNotNow" defaults="Not now" />
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
